import type { User } from '@devhacker/shared/features/user/types/user';
import AdminControlsPanel from '@/components/lh-ui/AdminControlsPanel/AdminControlsPanel.vue';
import TheContent from '~/components/content/TheContent';
import { Scripts } from '~/libs/Scripts';
import { getEditLinkHref, getFullUrl } from '~/utils';

import ArticleNote, { getArticleNoteProps } from '~/components/ArticleNote.amp.vue';
import type { TArticleNoteItem } from '~/types';
import { Quasar } from '~/libs/Quasar';
import WidgetsRendererContainer from '~/containers/WidgetsRendererContainer.vue';
import FullScreenGallery from '~/components/content/components/FullScreenGallery.vue';
import ArticleCardActions from '~/components/ArticleCardActions/ArticleCardActions.vue';
import ArticleCardActionsWithReactions from '~/components/ArticleCardActionsWithReactions/ArticleCardActionsWithReactions.vue';
import AuthorsExperts from '~/components/AuthorsExperts/AuthorsExperts.vue';
import { isAnyRecipePage, isHealthPath } from '~/utils/router';
import { ADS_EXCEPTIONS } from '~/constants/ads-labels';
import { getEditorialForAnalytics } from '~/utils/analyticsUtils';

import ArticleCard from '~/components/ArticleCard/ArticleCard.vue';
import CommentsContainer from '~/containers/CommentsContainer/CommentsContainer.vue';
import { useFoldersStore } from '~/store/folders';
import { useIndexStore } from '~/store';
import { usePageStore } from '~/store/pagesData';
import { useWidgetsStore } from '~/store/widgets';
import { useArticlesStore } from '~/store/articles';
import { useFavorite } from '@/composables/useFavoriteMethods';
import { useUpdatingViewsCount } from '@/composables/useUpdatingViewsCount';
import { useLightboxGallery } from '~/composables/useLightboxGallery';
import type { TContent, TWidget } from '@devhacker/shared/utils/adfox/types';
import { addIndexToAdfoxWidgets, getSplicedContent } from '@devhacker/shared/utils/adfox';

export default {
  name: 'GeneralTemplates',

  components: {
    AdminControlsPanel,
    ArticleNote,
    ArticleCardActions,
    ArticleCardActionsWithReactions,
    TheContent,
    WidgetsRendererContainer,
    FullScreenGallery,
    ArticleCard,
    CommentsContainer,
    AuthorsExperts,
  },

  setup() {
    const {
      onFavoriteDelete,
      onFavoriteCreateFolderAndAdd,
      onFavoriteCreateFolderAndTransfer,
      onFavoriteTransferToFolder,
      onFavoriteAddToFolder,
      onFavoriteDeferred,
    } = useFavorite();
    const { loadingViewsIds, updateViewsCount, getLoadingViews } = useUpdatingViewsCount();

    const articlesStore = useArticlesStore();
    const foldersStore = useFoldersStore();
    const indexStore = useIndexStore();
    const pageStore = usePageStore();
    const route = useRoute();
    const widgetsStore = useWidgetsStore();

    const article = pageStore.currentArticle;

    const {
      imagesSrc,
      imagesSrcset,
      currentImageId,
      isGalleryVisible,
      openGallery,
      closeGallery,
      checkImageClick,
    } = useLightboxGallery(article?.content ?? []);

    return {
      onFavoriteDelete,
      onFavoriteCreateFolderAndAdd,
      onFavoriteCreateFolderAndTransfer,
      onFavoriteTransferToFolder,
      onFavoriteAddToFolder,
      onFavoriteDeferred,
      loadingViewsIds,
      updateViewsCount,
      getLoadingViews,
      articlesStore,
      foldersStore,
      indexStore,
      pageStore,
      route,
      widgetsStore,
      //Gallery
      imagesSrc,
      imagesSrcset,
      currentImageId,
      isGalleryVisible,
      openGallery,
      closeGallery,
      checkImageClick,
    };
  },

  data(): {
    parentArticle: boolean;
    scripts: IScripts | null;
    isGalleryVisible: boolean;
  } {
    return {
      scripts: null,
      isGalleryVisible: false,
      parentArticle: true,
    };
  },

  computed: {
    user(): User | null {
      return this.indexStore.user;
    },

    favorites() {
      return this.articlesStore.getArticleFavorite(this.article?.id ?? 0);
    },

    folders(): IFolders {
      return this.foldersStore.$state;
    },

    isAnyRecipePage(): boolean {
      return isAnyRecipePage(this.route.name);
    },
    articleTitle(): string {
      return this.article?.title || '';
    },
    articleCardComponent(): string {
      return this.isAnyRecipePage ? 'ArticleCardActions' : 'ArticleCardActionsWithReactions';
    },
    editLinkHref(): string {
      return this.article ? getEditLinkHref(this.article.id) : '';
    },
    canHaveReactions(): boolean {
      return Boolean(this.article?.canHaveReactions);
    },
    viewsCount(): number {
      return this.articlesStore.getViewsCount(this.article?.id ?? 0);
    },
    isAuthenticatedInWordpress(): boolean {
      return this.indexStore.isAuthenticatedInWordpress;
    },
    content(): TContent[] {
      return this.article?.content || [];
    },
    slicedContents(): TContent[][] {
      return getSplicedContent(this.content);
    },
    isHealthPath(): boolean {
      return isHealthPath(this.route.path);
    },
    widgetsInsideContent(): (TWidget[] | undefined)[] {
      const widgetsStore = useWidgetsStore();

      // предотвращает ошибку гидратации, когда на клиенте вызывается addIndexToAdfoxWidgets
      // а внутри него nanoid генерирует другой класс
      return useState(`WIDGETS_INSIDE_CONTENT`, () => {
        const { length } = this.slicedContents;
        return this.slicedContents.map((_, index) => {
          if (length >= 2 && index === 0 && widgetsStore.currentAfterExcerptWidgets.length > 0) {
            return widgetsStore.currentAfterExcerptWidgets;
          }

          if (index === this.slicedContents.length - 1) {
            return undefined;
          }

          return length >= 3
            ? addIndexToAdfoxWidgets(widgetsStore.currentPostDynamicWidgets, index)
            : undefined;
        });
      }).value;
    },
    aboveCommentsWidgets(): TWidget[] {
      return this.widgetsStore.currentAboveCommentsWidgets;
    },
    article(): ISingleArticle | null {
      return this.pageStore.currentArticle;
    },
    articleLastModified(): string {
      return this.article && this.article.lastModified ? this.article.lastModified.toString() : '';
    },
    isWidgetsRendererVisible(): boolean {
      const { isPromoArticle, isJumperPost } = this;
      return !isPromoArticle && !isJumperPost;
    },
    isAdsException(): boolean {
      return ADS_EXCEPTIONS.includes(this.article?.advertLabel ?? '');
    },
    isPromoArticle(): boolean {
      return this.pageStore.isPromo;
    },
    isJumperPost(): boolean {
      return this.pageStore.isJumperPost;
    },
    isTemplateBroadside(): boolean {
      return this.pageStore.isTemplateBroadside;
    },
    isAdvertisingPost(): boolean {
      return this.pageStore.isAdvertisingPost;
    },
    isAds(): boolean {
      return this.isAdvertisingPost || this.isPromoArticle;
    },
    articleNotes(): Array<TArticleNoteItem> | null {
      if (!this.article) {
        return null;
      }

      const {
        meta: { customAuthors, source, cover_source, adsSource },
        isAdvertising,
        isPromo,
        advertLabel,
        caption: legacy_cover_source,
      } = this.article;

      const coverSourceWithFallback: IArticleMeta['cover_source'] = {
        ...cover_source,
        text: cover_source.text || legacy_cover_source,
      };

      return getArticleNoteProps(
        source,
        coverSourceWithFallback,
        customAuthors,
        'Над текстом работали:',
        adsSource,
        isAdvertising,
        isPromo,
        advertLabel,
      );
    },
    isSearchVisible(): boolean {
      return this.indexStore.visibleModalWindows.includes('search');
    },
    commentsCount(): number {
      return this.articlesStore.getCommentsCount(this.article?.id ?? 0);
    },
    showComments(): boolean {
      const isNotJumperPost = !this.isJumperPost;
      const commentsEnabledIsTrue = this.article?.commentsEnabled === true;

      return commentsEnabledIsTrue && isNotJumperPost;
    },
    singleArticleContainerStyle(): Record<string, string> | null {
      const { mainColor } = this.article?.meta || {};

      return mainColor
        ? {
            '--main-color': mainColor,
          }
        : null;
    },
    belowCommentsWidgets(): TWidget[] {
      return this.widgetsStore.currentBelowCommentsWidgets;
    },
  },

  async mounted() {
    if (this.article) {
      // +1 view
      this.$viewsApi.incrArticleViewsCount(this.article.id);

      this.scripts = new Scripts(
        this.article.scripts as TScript[],
        this.article.inlineScripts as TScript[],
      );
      if (this.scripts) {
        this.scripts.addScripts();
      }

      // Костыль для adfox, неправильно расчитывает отступ от меню
      if (this.article.template === 'two-column' && this.article.isAdvertising) {
        document.body.classList.add('one-level-menu');
      }

      this.sendEditorialAnalytics();
    }

    Quasar.addMessageListener();
    document.documentElement.addEventListener('click', (event) => this.handleReadAlsoClick(event), {
      passive: true,
    });
  },

  beforeUnmount() {
    Quasar.removeMessageListener();
    if (this.scripts) this.scripts.removeScripts();
  },

  methods: {
    getEditLinkHref,

    sendEditorialAnalytics() {
      if (!this.article) {
        return;
      }

      this.$sendCustomYandexMetrika({
        'Просмотр рубрики': this.article.categories.map((category) => category.title).join(','),
      });

      this.$sendCustomYandexMetrika({
        'Просмотр мини-редакции': getEditorialForAnalytics(this.article),
      });
    },

    onClickOnCategory(link: string, titleCategory: string, _title: string): void {
      this.$sendYandexMetrika({
        level1: 'Выбор рубрики',
        level4: getFullUrl(link),
        level5: titleCategory,
        level6: 'unknown',
      });
    },

    handleReadAlsoClick(event: MouseEvent) {
      const isClickedInsideReadAlso = event.target?.closest(
        `[location="twenty-footer"][id^=adfox]`,
      );
      if (!isClickedInsideReadAlso) return;

      this.$sendYandexMetrika({ level1: 'Клик_РСЯ' });
    },
  },
};
